import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Button,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { sharePointService } from '../../services/sharePointService';
import authApi from '../../services/authApi';
import { useUserSession } from '../../contexts/UserContext';
import NavigationDrawer from '../Navigation/NavigationDrawer';
import { useNavigate } from 'react-router-dom';

const SharePointReport = () => {
  const { userSession } = useUserSession();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const fetchReport = async () => {
    try {
      if (!userSession?.effectiveEmail && !userSession?.email) {
        console.log('No user session available');
        return;
      }

      setLoading(true);
      setError(null);
      const token = authApi.getToken();
      const data = await sharePointService.getSecurityReport(token);
      setReport(data);
    } catch (error) {
      console.error('Error fetching security report:', error);
      setError(error.message || 'Failed to fetch security report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userSession?.effectiveEmail || userSession?.email) {
      fetchReport();
    }
  }, [userSession?.effectiveEmail, userSession?.email, userSession]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
        <NavigationDrawer
          isMobile={isMobile}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          activeSection={''}
          setActiveSection={() => {}}
          navigate={navigate}
        />
        <Box component="main" sx={{ flexGrow: 1, p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
        <NavigationDrawer
          isMobile={isMobile}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          activeSection={''}
          setActiveSection={() => {}}
          navigate={navigate}
        />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Alert 
            severity="error" 
            sx={{ mt: 2 }}
            action={
              <Button color="inherit" size="small" onClick={fetchReport}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
        <NavigationDrawer
          isMobile={isMobile}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          activeSection={''}
          setActiveSection={() => {}}
          navigate={navigate}
        />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Alert severity="info" sx={{ mt: 2 }}>
            No security report data available.
          </Alert>
        </Box>
      </Box>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getRiskLevelColor = (level) => {
    switch (level.toLowerCase()) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
      <NavigationDrawer
        isMobile={isMobile}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        activeSection={''}
        setActiveSection={() => {}}
        navigate={navigate}
      />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          SharePoint Security Report - Prototype Not Functional Yet
        </Typography>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Security Overview
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Security Score
                </Typography>
                <Chip 
                  label={`${report.summary.securityScore}%`}
                  color={getScoreColor(report.summary.securityScore)}
                  size="large"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Risk Level
                </Typography>
                <Chip 
                  label={report.summary.riskLevel}
                  color={getRiskLevelColor(report.summary.riskLevel)}
                  size="large"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Critical Issues
                </Typography>
                <Chip 
                  label={report.summary.criticalIssues}
                  color={report.summary.criticalIssues > 0 ? 'error' : 'success'}
                  size="large"
                />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Recommendations
          </Typography>
          
          {report.recommendations.critical.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" color="error" gutterBottom>
                Critical Actions
              </Typography>
              <List>
                {report.recommendations.critical.map((rec, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={rec} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              Best Practices
            </Typography>
            <List>
              {report.recommendations.bestPractices.map((practice, index) => (
                <ListItem key={index}>
                  <ListItemText primary={practice} />
                </ListItem>
              ))}
            </List>
          </Box>

          {report.findings.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Detailed Findings
              </Typography>
              <List>
                {report.findings.map((finding, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={finding.issue}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.primary">
                            Severity: <Chip 
                              label={finding.severity}
                              color={getRiskLevelColor(finding.severity)}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </Typography>
                          <Typography variant="body2">{finding.details}</Typography>
                          <Typography variant="body2" color="primary">
                            Recommendation: {finding.recommendation}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default SharePointReport; 